import { ChangeEvent, ForwardedRef, forwardRef, useState } from 'react';
import styled from 'styled-components';
import { CharacterCount } from '../../character_count';
import type { IconType } from '../../icon';
import { Description } from '../base/description.component';
import { HelpText } from '../base/help_text.component';
import { Label } from '../base/label.component';
import { TextArea, TextAreaProps } from '../base/text_area.component';
import { theme } from '../common_input.theme';
import { useInput } from '../use_input.hook';

export type InputTextAreaRootProps = {
	/**
	 * The label of the text area.
	 */
	label: string;
	name?: string;

	/**
	 * Use to visually hide the label.
	 */
	hideLabel?: boolean;

	/**
	 * Use to add a descriptive text below the text area.
	 */
	helperText?: string;

	/**
	 * An optional leading icon shown before the `helperText`. Ignored while the
	 * field is `invalid`, in which case the invalid warning icon takes over.
	 */
	helperIcon?: IconType;

	/**
	 * It's mandatory if `invalid` is set to `true` and only visible in that case
	 * too. The purpose with this is to provide a explanation why the text area is
	 * `invalid`. This text will replace the `helperText`, right below the text
	 * area.
	 */
	invalidText?: string;

	/**
	 * The maximum amount of characters allowed by the input
	 * Keep in mind that you still need to handle validation with `invalid` and `invalidText`
	 */
	characterCount?: number;

	/**
	 * This determines if the character-count should be shown even if the
	 * `characterCount` value has not been exceeded
	 */
	alwaysShowCharacterCount?: boolean;
	description?: string;
	optional?: boolean;
} & TextAreaProps;

const elements = {
	container: styled.div`
		display: flex;
		flex-direction: column;
		flex: 1 1 auto;
	`,
	label: styled(Label)`
		margin-bottom: ${theme.getValue('labelMarginBottom')};
	`,
	description: styled(Description)`
		margin-bottom: ${theme.getValue('descriptionMarginBottom')};
	`,
	footer: styled.div`
		display: flex;
		justify-content: space-between;
		gap: ${theme.getValue('helpTextGap')};
	`,
};

export const CellulaInputTextAreaRoot = forwardRef(function CellulaInputTextAreaRoot(
	{
		id,
		name,
		label,
		hideLabel,
		required,
		invalid,
		invalidText,
		helperText,
		helperIcon,
		readOnly,
		onChange,
		onBlur,
		defaultValue,
		characterCount,
		alwaysShowCharacterCount,
		description,
		optional,
		...rest
	}: InputTextAreaRootProps,
	ref: ForwardedRef<HTMLTextAreaElement>,
): JSX.Element {
	const [inputValue, setInputValue] = useState<string>(defaultValue?.toString() ?? '');
	function handleInputChange(event: ChangeEvent<HTMLTextAreaElement>): void {
		setInputValue(event.target.value);

		onChange?.(event);
	}

	const {
		props: inputProps,
		labelProps,
		descriptionProps,
		helpTextProps,
	} = useInput<HTMLTextAreaElement>(
		{
			id,
			name,
			label,
			hideLabel,
			required,
			invalid,
			invalidText,
			helperText,
			defaultValue,
			readOnly,
			onChange: handleInputChange,
			onBlur,
			description,
			optional,
		},
		ref,
	);

	return (
		<elements.container>
			{/** biome-ignore lint/a11y/noLabelWithoutControl: suppressed due to migration - should be fixed */}
			<elements.label {...labelProps} />
			<elements.description {...descriptionProps} />

			<TextArea defaultValue={defaultValue} {...rest} {...inputProps} />

			<elements.footer>
				<HelpText {...helpTextProps} icon={helperIcon} />
				<CharacterCount value={inputValue} limit={characterCount} alwaysShow={alwaysShowCharacterCount} />
			</elements.footer>
		</elements.container>
	);
});
